import {
  Button,
  Flex,
  message,
  Modal,
  Space,
  Table,
  TablePaginationConfig,
  Upload,
} from "antd";
import { useMemo, useState } from "react";
import {
  CSV_ACCEPTED_TYPES,
  CSV_COLUMN_MAPPING,
  CSV_MAX_SIZE,
} from "../../constants/salesOverviewConstants";
import dayjs, { Dayjs } from "dayjs";
import { useImportSales } from "../../hooks/useSales";
import { getJsonData, getNecessaryData } from "../../utils/file";
import { getTableColumns } from "./utils/table";
import { getTablePagination } from "@/utils/table";
import { DEFAULT_PAGINATION } from "@/constants/table";

type FileMeta = {
  data: Record<string, string>[];
  originalFile: File;
};

interface ImportCsvProps {
  onImportSuccess?: () => void;
}

const ImportCsv = ({ onImportSuccess }: ImportCsvProps) => {
  const [pagination, setPagination] =
    useState<TablePaginationConfig>(DEFAULT_PAGINATION);
  const [fileMeta, setFileMeta] = useState<FileMeta>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  const handleFileUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      message.error("Please upload a CSV file");
      return false;
    }

    if (file.size > CSV_MAX_SIZE) {
      const maxSizeInMB = CSV_MAX_SIZE / 1024 / 1024;
      message.error(`File size must be less than ${maxSizeInMB}MB`);
      return false;
    }

    const jsonData = await getJsonData(file, CSV_COLUMN_MAPPING);
    const filteredData = getNecessaryData(jsonData);

    message.success(
      `Successfully loaded ${filteredData.length} records from CSV`
    );

    setFileMeta({
      data: filteredData,
      originalFile: file,
    });
    setIsModalOpen(true);
  };

  const handleImport = () => {
    if (!fileMeta) {
      return;
    }

    importCsv(fileMeta.originalFile);
  };

  const { mutate: importCsv, isPending } = useImportSales({
    onSuccess: () => {
      onImportSuccess?.();
      setFileMeta(undefined);
      setIsModalOpen(false);
      setDateRange(null);

      message.success(`Sales data uploaded successfully`);
    },
  });

  const handleClear = () => {
    setFileMeta(undefined);
    setIsModalOpen(false);
    setDateRange(null);

    message.success(`CSV file cleared`);
  };

  // Filter data based on date range
  const filteredData = useMemo(() => {
    const data = fileMeta?.data;
    if (!data) return [];

    if (!dateRange) return data;

    return data.filter((item) => {
      const date = dayjs(item["Creation Date"], "DD-MMM-YY hh:mm A");
      return date.isAfter(dateRange[0]) && date.isBefore(dateRange[1]);
    });
  }, [fileMeta?.data, dateRange]);

  return (
    <>
      {fileMeta ? (
        <Button onClick={() => setIsModalOpen(true)}>Preview CSV File</Button>
      ) : (
        <Upload
          accept={CSV_ACCEPTED_TYPES}
          beforeUpload={handleFileUpload}
          showUploadList={false}
        >
          <Button type="primary">Import CSV</Button>
        </Upload>
      )}

      <Modal
        title="Preview CSV File"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        // style={{ top: 20, paddingLeft: 8, paddingRight: 8 }}
        styles={{
          body: {
            paddingTop: 8,
          },
        }}
        centered
        width="max-content"
      >
        <Flex vertical gap={12}>
          <Flex gap={16} wrap="wrap" align="center" justify="end">
            {/* <DatePicker.RangePicker
              placeholder={["Start Date", "End Date"]}
              value={dateRange}
              style={{ width: "fit-content" }}
              onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
              allowClear
            /> */}

            <Space>
              <Button danger onClick={() => handleClear()}>
                Clear
              </Button>
              <Button
                key="import"
                type="primary"
                onClick={handleImport}
                loading={isPending}
              >
                Import
              </Button>
            </Space>
          </Flex>

          {filteredData && (
            <Table
              columns={getTableColumns()}
              dataSource={filteredData.map((item, index) => ({
                ...item,
                key: index,
              }))}
              pagination={getTablePagination({
                pagination,
                label: "sales",
                onChange: (page, pageSize) => {
                  setPagination({
                    ...pagination,
                    current: page,
                    pageSize,
                  });
                },
              })}
              scroll={{
                x: "max-content",
                // y: 55 * ((pagination.pageSize || 10) - 5),
                y: "70vh",
              }}
              size="small"
              tableLayout="auto"
            />
          )}
        </Flex>
      </Modal>
    </>
  );
};

export default ImportCsv;
